"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import PurchasePartiesListPrint from "@/components/print/PurchasePartiesListPrint";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { rmcClient } from "@/lib/rmcClient";
import { gstSchema } from "@/lib/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";

const DEFAULT_FORM = {
  name: "",
  gst_number: "",
  address: "",
  contact_no: "",
  is_active: true,
};

export default function RmcPurchaseParties() {
  const qc = useQueryClient();
  const printRef = React.useRef(null);
  const [pdfBusy, setPdfBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState("create");
  const [selectedId, setSelectedId] = React.useState(null);
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [form, setForm] = React.useState(DEFAULT_FORM);
  const isActiveFilter = statusFilter === "active";

  const {
    data: rows = [],
    isLoading: isRowsLoading,
    isFetching: isRowsFetching,
  } = useQuery({
    queryKey: ["purchaseParties", isActiveFilter],
    queryFn: async () => (await rmcClient.listPurchaseParties({ is_active: isActiveFilter })).data,
  });

  const save = useMutation({
    mutationFn: (payload) => rmcClient.createPurchaseParty(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchaseParties"] });
      toast.success("Party added");
      setForm(DEFAULT_FORM);
      setOpen(false);
    },
  });

  const update = useMutation({
    mutationFn: ({ id, payload }) => rmcClient.updatePurchaseParty(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchaseParties"] });
      toast.success("Party updated");
      setForm(DEFAULT_FORM);
      setSelectedId(null);
      setOpen(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deletePurchaseParty(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchaseParties"] });
      toast.success("Party deleted");
    },
  });

  const openCreate = () => {
    setMode("create");
    setSelectedId(null);
    setForm(DEFAULT_FORM);
    setOpen(true);
  };

  const openEdit = async (id) => {
    try {
      setMode("edit");
      setSelectedId(id);
      setOpen(true);
      const party = (await rmcClient.getPurchaseParty(id, { include_inactive: true })).data;
      setForm({
        name: party?.name ?? "",
        gst_number: party?.gst_number ?? "",
        address: party?.address ?? "",
        contact_no: party?.contact_no ?? "",
        is_active: Boolean(party?.is_active),
      });
    } catch {
      toast.error("Failed to load party details");
      setOpen(false);
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.gst_number && !gstSchema.safeParse(form.gst_number).success) {
      toast.error("Invalid GST");
      return;
    }
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      contact_no: form.contact_no.trim(),
      gst_number: form.gst_number.trim().toUpperCase(),
      is_active: Boolean(form.is_active),
    };
    if (mode === "create") {
      save.mutate(payload);
      return;
    }
    if (!selectedId) return;
    update.mutate({ id: selectedId, payload });
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this party? This action cannot be undone.")) return;
    remove.mutate(id);
  };

  const exportPurchasePartiesPdf = async () => {
    setPdfBusy(true);
    try {
      await printRef.current?.openPrint?.({ is_active: isActiveFilter });
    } catch {
      toast.error("Could not prepare PDF export");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PurchasePartiesListPrint ref={printRef} />
      <PageBreadCrumb pageTitle="Purchase Party Master" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={statusFilter === "active" ? "primary" : "outline"}
            onClick={() => setStatusFilter("active")}
          >
            Active
          </Button>
          <Button
            size="sm"
            variant={statusFilter === "inactive" ? "primary" : "outline"}
            onClick={() => setStatusFilter("inactive")}
          >
            Inactive
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={pdfBusy || isRowsLoading}
            onClick={exportPurchasePartiesPdf}
          >
            {pdfBusy ? "Preparing…" : "Export PDF"}
          </Button>
          <Button size="sm" onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>
      <DataTable
        columns={[
          { key: "name", header: "Name" },
          { key: "contact_no", header: "Contact No" },
          { key: "gst_number", header: "GST" },
          { key: "address", header: "Address" },
          {
            key: "is_active",
            header: "Status",
            render: (row) => (row.is_active ? "Active" : "Inactive"),
          },
          {
            key: "actions",
            header: "Actions",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (row) => (
              <div className="flex w-full items-center justify-end gap-2">
                <button
                  type="button"
                  aria-label="View or edit party"
                  title="View / Edit"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(row.id);
                  }}
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path
                      d="M2.5 10s2.7-4.5 7.5-4.5S17.5 10 17.5 10s-2.7 4.5-7.5 4.5S2.5 10 2.5 10Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </button>
                <button
                  type="button"
                  aria-label="Delete party"
                  title="Delete"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                  disabled={remove.isPending}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(row.id);
                  }}
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path
                      d="M7.5 3.5h5M4.5 5.5h11M6.5 5.5l.6 9a1 1 0 0 0 1 .9h3.8a1 1 0 0 0 1-.9l.6-9M8.5 8.5v4.5M11.5 8.5v4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isRowsLoading || isRowsFetching}
        emptyMessage={
          statusFilter === "active"
            ? "No active purchase parties found."
            : "No inactive purchase parties found."
        }
      />
      <DrawerPanel
        open={open}
        onClose={() => setOpen(false)}
        title={mode === "create" ? "Add party" : "View / Edit party"}
      >
        <div className="space-y-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1"
            />
          </div>
          <div>
            <Label>GST</Label>
            <Input
              value={form.gst_number}
              onChange={(e) =>
                setForm({ ...form, gst_number: e.target.value.toUpperCase() })
              }
              className="mt-1"
            />
          </div>
          <div>
            <Label>Contact No</Label>
            <Input
              value={form.contact_no}
              onChange={(e) => setForm({ ...form, contact_no: e.target.value })}
              className="mt-1"
              placeholder="e.g. 9999999999"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Input
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="mt-1"
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <div className="grid grid-cols-2 gap-2">
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                  form.is_active
                    ? "border-white/70 bg-white/10 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                }`}
              >
                <input
                  type="radio"
                  name="partyStatus"
                  checked={form.is_active}
                  onChange={() => setForm({ ...form, is_active: true })}
                  className="h-4 w-4"
                />
                Active
              </label>
              <label
                className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-3 text-sm font-medium transition ${
                  !form.is_active
                    ? "border-white/70 bg-white/10 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                }`}
              >
                <input
                  type="radio"
                  name="partyStatus"
                  checked={!form.is_active}
                  onChange={() => setForm({ ...form, is_active: false })}
                  className="h-4 w-4"
                />
                Inactive
              </label>
            </div>
          </div>
          <Button
            size="md"
            className="mt-2 w-full"
            disabled={save.isPending || update.isPending}
            onClick={handleSave}
          >
            {save.isPending || update.isPending
              ? mode === "create"
                ? "Saving..."
                : "Updating..."
              : mode === "create"
                ? "Save"
                : "Update"}
          </Button>
        </div>
      </DrawerPanel>
    </div>
  );
}
